import useHelpers from "composables/useHelpers.js";
import axios from "axios";

export const DEFAULT_GROUP_MEETING = {
  id: null,
  name: "",
  meeting_type: "group_meeting",
  message: "",
  school_id: null,
  status: "concept",
  teacher_percentage: 50,
  timeframe_settings: {},
  mentor_classes: [],
  rating_type_ids: [],
  rating_types: [],
  teacher_ids: {},
  teacher_scores: [],
  total_given_ratings: 0,
  total_ratings: 0,
};

export class GroupMeeting {
  constructor(data = {}) {
    this.id = data.id ?? DEFAULT_GROUP_MEETING.id;
    this.meeting_type = data.meeting_type ?? DEFAULT_GROUP_MEETING.meeting_type;
    this.name = data.name ?? DEFAULT_GROUP_MEETING.name;
    this.message = data.message ?? DEFAULT_GROUP_MEETING.message;
    this.school_id = data.school_id ?? DEFAULT_GROUP_MEETING.school_id;
    this.status = data.status ?? DEFAULT_GROUP_MEETING.status;
    this.teacher_percentage =
      data.teacher_percentage ?? DEFAULT_GROUP_MEETING.teacher_percentage;
    this.timeframe_settings =
      data.timeframe_settings ?? DEFAULT_GROUP_MEETING.timeframe_settings;
    this.mentor_classes =
      data.mentor_classes ?? DEFAULT_GROUP_MEETING.mentor_classes;
    this.rating_type_ids =
      data.rating_type_ids ?? DEFAULT_GROUP_MEETING.rating_type_ids;
    this.rating_types = data.rating_types ?? DEFAULT_GROUP_MEETING.rating_types;
    this.teacher_ids = data.teacher_ids ?? DEFAULT_GROUP_MEETING.teacher_ids;
    this.teacher_scores =
      data.teacher_scores ?? DEFAULT_GROUP_MEETING.teacher_scores;
    this.total_given_ratings =
      data.total_given_ratings ?? DEFAULT_GROUP_MEETING.total_given_ratings;
    this.total_ratings =
      data.total_ratings ?? DEFAULT_GROUP_MEETING.total_ratings;
  }

  async store() {
    const { schoolId } = useHelpers();

    try {
      let { teacher_ids } = this;

      teacher_ids = Object.fromEntries(
        Object.entries(teacher_ids).filter(([_, value]) => value.length),
      );

      await axios.post(`/api/school/${schoolId}/group-meetings`, {
        ...this,
        ...{ teacher_ids },
      });
    } catch (error) {
      throw new Error(error);
    }
  }

  async updateGlobalInfo() {
    try {
      const { data } = axios.post(
        `/api/school/${this.school_id}/group-meetings/${this.id}/update-global-info`,
        {
          name: this.name,
          message: this.message,
        },
      );

      return data;
    } catch (error) {
      throw new Error(error);
    }
  }

  async updateRating(rating) {
    try {
      return await axios.put(
        `/api/school/${this.school_id}/group-meetings/${this.id}/rating/${rating.id}`,
        {
          rating: rating.rating,
          rating_remark: rating.rating_remark,
        },
      );
    } catch (error) {
      throw new Error(error);
    }
  }

  async addChat(chatData) {
    const { mentor_class } = chatData;
    try {
      const { data } = await axios.post(
        `api/school/${this.school_id}/group-meetings/${this.id}/chat`,
        chatData,
      );

      const mentorClass = this.mentor_classes.find(
        (_) => _.title === mentor_class,
      );

      if (mentorClass) {
        mentorClass.chats.push(data);
      }

      return data;
    } catch (error) {
      throw new Error(error);
    }
  }

  async updatePlanning() {
    const { schoolId } = useHelpers();

    try {
      const { data } = await axios.post(
        `api/school/${schoolId}/group-meetings/${this.id}/update-planning`,
        {
          timeframe_settings: this.timeframe_settings,
        },
      );

      return data;
    } catch (error) {
      throw new Error(error);
    }
  }

  async addGoal({ goal = "", mentorClass = null } = {}) {
    try {
      const { data } = await axios.post(
        `/api/school/${this.school_id}/group-meetings/${this.id}/agreements`,
        {
          mentor_class: mentorClass,
          type: "goal",
          value: goal,
        },
      );

      const mentorclass = this.mentor_classes.find(
        (_) => _.title === mentorClass,
      );

      mentorclass.goals = data;

      return data;
    } catch (error) {
      throw new Error(error);
    }
  }

  async deleteGoal({ id = null } = {}) {
    try {
      const { data } = await axios.delete(
        `/api/school/${this.school_id}/group-meetings/${this.id}/agreements/${id}`,
      );

      this.mentor_classes.forEach((mentorClass) => {
        const goalIndex = mentorClass.goals.data.findIndex(
          (goal) => goal.id === id,
        );

        if (goalIndex !== -1) {
          mentorClass.goals = data;
        }
      });
    } catch (error) {
      throw new Error(error);
    }
  }

  async updateGoal({ id = null, value = "" } = {}) {
    try {
      const { data } = await axios.put(
        `/api/school/${this.school_id}/group-meetings/${this.id}/agreements/${id}`,
        {
          value: value,
        },
      );

      this.mentor_classes.forEach((mentorClass) => {
        const goalIndex = mentorClass.goals.data.findIndex(
          (goal) => goal.id === id,
        );

        if (goalIndex !== -1) {
          mentorClass.goals = data;
        }
      });
      return data;
    } catch (error) {
      throw new Error(error);
    }
  }

  async addAgreement({ agreement = "", mentorClass = null } = {}) {
    try {
      const { data } = await axios.post(
        `/api/school/${this.school_id}/group-meetings/${this.id}/agreements`,
        {
          mentor_class: mentorClass,
          type: "agreement",
          value: agreement,
        },
      );

      const mentorclass = this.mentor_classes.find(
        (mClass) => mClass.title === mentorClass,
      );

      mentorclass.agreements = data;

      return data;
    } catch (error) {
      throw new Error(error);
    }
  }

  async updateAgreement({ id = null, value = "" } = {}) {
    try {
      const { data } = await axios.put(
        `/api/school/${this.school_id}/group-meetings/${this.id}/agreements/${id}`,
        {
          value: value,
        },
      );

      this.mentor_classes.forEach((mentorClass) => {
        const agreementIndex = mentorClass.agreements.data.findIndex(
          (agreement) => agreement.id === id,
        );

        if (agreementIndex !== -1) {
          mentorClass.agreements = data;
        }
      });

      return data;
    } catch (error) {
      throw new Error(error);
    }
  }

  async deleteAgreement({ id = null } = {}) {
    try {
      const { data } = await axios.delete(
        `/api/school/${this.school_id}/group-meetings/${this.id}/agreements/${id}`,
      );

      this.mentor_classes.forEach((mentorClass) => {
        const agreementIndex = mentorClass.agreements.data.findIndex(
          (agreement) => agreement.id === id,
        );

        if (agreementIndex !== -1) {
          mentorClass.agreements = data;
        }
      });
    } catch (error) {
      throw new Error(error);
    }
  }
}
